/**
 * Final step — educational hero + cards, then the completion CTA that marks
 * join onboarding done and hands the user off to their workspace dashboard.
 */
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuthInfo } from '@/allauth_auth/hooks';
import { useInvalidateUserProfile } from '@/hooks/useUserProfileData';
import { Button } from '@/lib/base/buttons/button';
import { Card, CardBody } from '@/lib/base/card';
import { HintText } from '@/lib/base/forms';

import type {
  JoinOnboardingCompletePayload,
  JoinOnboardingStatus,
} from '../../api';
import { joinOnboardingStatusKey, useCompleteJoinOnboarding } from '../../api';
import { dashboardPath } from '../../constants';
import { useJoinFlowState } from '../../flowState';

const LEARN_CARDS = [
  {
    heading: 'Your follow-ups write themselves',
    body: 'Ahoy preps from your email and drafts follow-ups with context, ready for your approval.',
  },
  {
    heading: 'Ready before every meeting',
    body: 'Briefs assembled automatically – touchpoints, pricing, next steps.',
  },
  {
    heading: 'Nothing falls through',
    body: 'Calls, emails, and next steps captured and structured – without you touching a field.',
  },
] as const;

export function LearnStep() {
  const { orgId } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state, clearPersisted } = useJoinFlowState();
  const { user } = useAuthInfo();
  const userId: number | undefined = user?.id;
  const invalidateProfile = useInvalidateUserProfile();
  const { mutate, isPending, isSuccess, isError } = useCompleteJoinOnboarding(
    orgId ?? ''
  );

  const payload = useMemo<JoinOnboardingCompletePayload>(
    () => ({
      // Trimmed, and sent even when blank: the server treats an empty string as
      // "leave the stored value untouched" rather than blanking a name the admin
      // set, so there is no need to conditionally omit the keys here.
      first_name: state.firstName.trim(),
      last_name: state.lastName.trim(),
      role: state.role.trim(),
      timezone: state.timezone,
      mailbox_visibility: state.mailboxVisibility,
      filtered_emails: state.filteredEmails,
      filtered_domains: state.filteredDomains,
    }),
    [state]
  );

  const handleContinue = useCallback(() => {
    if (!orgId) {
      return;
    }
    mutate(payload, {
      onSuccess: data => {
        // Store the status the server actually persisted — NOT an unconditional
        // `completed: true`. complete() is a no-op for a non-pending membership
        // and echoes back the real status, so forcing `completed` here could let
        // the dashboard guard bypass still-required onboarding.
        // Must use the SAME (org, user)-scoped key the status hook reads, or this
        // write lands on a key nothing observes and the dashboard guard bounces the
        // user straight back into the wizard they just finished.
        queryClient.setQueryData<JoinOnboardingStatus>(
          joinOnboardingStatusKey(orgId, userId),
          data
        );
        // Drop the persisted wizard draft now that onboarding is done, so a
        // later user in the same tab can't have this user's role and mailbox
        // exclusions reloaded by JoinFlowProvider (logout clears the auth
        // session but not this sessionStorage key).
        clearPersisted();
        // Completion persisted the name/timezone onto the CustomUser server-side,
        // but the cached ['userProfile'] query (5-min staleTime) still holds the
        // pre-onboarding values. Invalidate it so the dashboard the user lands on
        // shows the name/timezone they just set rather than the stale profile.
        void invalidateProfile();
        navigate(dashboardPath(orgId));
      },
    });
  }, [
    orgId,
    userId,
    mutate,
    payload,
    queryClient,
    clearPersisted,
    invalidateProfile,
    navigate,
  ]);

  return (
    <div data-testid='join-step-learn' className='flex flex-col gap-8'>
      <div className='flex flex-col gap-2'>
        <h2 className='font-display text-2xl font-medium text-[var(--color-text-primary)]'>
          Most CRMs were built to remember. Ahoy was built to act.
        </h2>
        <p className='text-base text-[var(--color-text-tertiary)]'>
          You show up to every conversation ready, not catching up.
        </p>
      </div>

      <div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
        {LEARN_CARDS.map(card => (
          <Card key={card.heading} variant='default'>
            <CardBody>
              <h3 className='text-base font-medium text-[var(--color-text-primary)]'>
                {card.heading}
              </h3>
              <p className='mt-1 text-[var(--color-text-secondary)]'>
                {card.body}
              </p>
            </CardBody>
          </Card>
        ))}
      </div>

      <div className='flex flex-col gap-4'>
        <Button
          size='lg'
          className='w-full'
          onClick={handleContinue}
          isDisabled={isPending}
          disabledTooltip='Setting up your workspace…'
          isLoading={isPending || isSuccess}
          showTextWhileLoading
        >
          Continue to my workspace
        </Button>
        {isError && (
          <div role='alert'>
            <HintText isInvalid showError className='text-center'>
              We couldn't finish setting up your workspace. Try again, and get
              in touch if it keeps happening.
            </HintText>
          </div>
        )}
      </div>
    </div>
  );
}
